import {
	EyeInvisibleOutlined,
	EyeOutlined,
	PictureOutlined,
} from "@ant-design/icons";
import { formatPrice } from "@di-devs/utils-fe";
import { Badge, Image, Space, Table, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import type React from "react";
import type { EventOccasion } from "../../types";

export interface EventOccasionsTableViewProps {
	occasions: EventOccasion[];
	loading?: boolean;
	imageBaseUrl?: string;
	primaryColor?: string;
	onEventClick?: (occasion: EventOccasion) => void;
	showDetailedColumns?: boolean;
}

export const EventOccasionsTableView: React.FC<
	EventOccasionsTableViewProps
> = ({
	occasions,
	loading = false,
	imageBaseUrl = "",
	primaryColor = "#99CA29",
	onEventClick,
	showDetailedColumns = false,
}) => {
	const formatDate = (date: string | null | undefined) => {
		if (!date) return "－";
		try {
			const d = dayjs(date);
			return (
				<div className="flex flex-col">
					<span className="text-xs">{d.format("YYYY年")}</span>
					<span>{d.format("MM月DD日")}</span>
				</div>
			);
		} catch {
			return "－";
		}
	};

	const baseColumns: ColumnsType<EventOccasion> = [
		{
			title: "ID",
			key: "id",
			dataIndex: "id",
			width: 80,
			align: "center",
		},
		{
			title: "表示",
			key: "isDisplayed",
			dataIndex: "isDisplayed",
			width: 80,
			align: "center",
			render: (isDisplayed: boolean) =>
				isDisplayed ? (
					<EyeOutlined style={{ color: primaryColor }} />
				) : (
					<EyeInvisibleOutlined className="text-gray-400" />
				),
		},
		{
			title: "画像",
			key: "image",
			dataIndex: "image",
			width: 120,
			align: "center",
			render: (image: string | undefined | null, record) => {
				const gallery = (record as EventOccasion).gallery as
					| string[]
					| undefined;
				const imageCount = gallery?.length || (image ? 1 : 0);

				if (!image) {
					return (
						<Image
							preview={false}
							src="/no-image.png"
							width={60}
							height={60}
							className="object-cover"
							alt={record.title}
						/>
					);
				}

				const imageUrl =
					image.startsWith("http://") ||
					image.startsWith("https://") ||
					image.startsWith("/")
						? image
						: `${imageBaseUrl}${image}`;

				if (imageCount > 1) {
					const galleryUrls =
						gallery?.map((url) =>
							url.startsWith("http://") ||
							url.startsWith("https://") ||
							url.startsWith("/")
								? url
								: `${imageBaseUrl}${url}`,
						) || [];

					return (
						<Tooltip
							title={
								<Space direction="vertical" size={4}>
									<span className="font-bold">{imageCount}枚の画像</span>
									<Image.PreviewGroup>
										<Space wrap size={4}>
											{galleryUrls.slice(0, 4).map((url, idx) => (
												<Image
													key={idx}
													src={url}
													fallback="/no-image.png"
													width={40}
													height={40}
													className="object-cover"
													alt={`${record.title} - ${idx + 1}`}
												/>
											))}
											{imageCount > 4 && (
												<span className="text-white">+{imageCount - 4}</span>
											)}
										</Space>
									</Image.PreviewGroup>
								</Space>
							}
							color="#fff"
							overlayInnerStyle={{ backgroundColor: "#333" }}
						>
							<Badge
								count={imageCount}
								size="small"
								style={{ backgroundColor: primaryColor }}
							>
								<Image.PreviewGroup items={galleryUrls}>
									<Image
										preview={{ mask: <PictureOutlined /> }}
										src={imageUrl}
										fallback="/no-image.png"
										width={60}
										height={60}
										className="object-cover cursor-pointer"
										alt={record.title}
									/>
								</Image.PreviewGroup>
							</Badge>
						</Tooltip>
					);
				}

				return (
					<Image
						preview
						src={imageUrl}
						fallback="/no-image.png"
						width={60}
						height={60}
						className="object-cover"
						alt={record.title}
					/>
				);
			},
		},
		{
			title: "イベントタイトル",
			key: "eventTitle",
			dataIndex: "title",
			width: 200,
			render: (title: string, record) => (
				<Tooltip title={title}>
					{onEventClick ? (
						<button
							type="button"
							className="cursor-pointer underline bg-transparent border-none p-0 text-inherit"
							style={{ color: primaryColor }}
							onClick={() => onEventClick(record)}
						>
							{title}
						</button>
					) : (
						<span style={{ color: primaryColor }}>{title}</span>
					)}
				</Tooltip>
			),
		},
		{
			title: "イベントプログラム",
			key: "programTitle",
			dataIndex: "programTitle",
			width: 200,
			render: () => "－",
		},
		{
			title: "予約開始",
			key: "reservationStart",
			dataIndex: "startDate",
			width: 150,
			align: "center",
			render: (date: string | null | undefined) => formatDate(date),
		},
		{
			title: "予約終了",
			key: "reservationEnd",
			dataIndex: "endDate",
			width: 150,
			align: "center",
			render: (date: string | null | undefined) => formatDate(date),
		},
		{
			title: "開始",
			key: "start",
			dataIndex: "start",
			width: 150,
			align: "center",
			render: (date: string | null | undefined) => formatDate(date),
		},
		{
			title: "終了",
			key: "end",
			dataIndex: "end",
			width: 150,
			align: "center",
			render: (date: string | null | undefined) => formatDate(date),
		},
		{
			title: "予約数",
			key: "reservationCount",
			dataIndex: "sumExpected",
			width: 120,
			align: "center",
			render: (count: number, record) => (
				<div className="bg-gray-100 p-2">
					<div>{count || 0}人</div>
					<div className="text-xs text-gray-400">
						({record.maxCapacity || 0}人)
					</div>
				</div>
			),
		},
		{
			title: "参加数",
			key: "participationCount",
			dataIndex: "sumAttended",
			width: 120,
			align: "center",
			render: (count: number | undefined) => (
				<div className="bg-gray-100 p-2">{count || 0}人</div>
			),
		},
	];

	const detailedColumns: ColumnsType<EventOccasion> = [
		...baseColumns,
		{
			title: "チェックイン",
			key: "checkIn",
			dataIndex: "checkIn",
			width: 100,
			align: "center",
			render: (checkIn: string | undefined) => checkIn || "QR",
		},
		{
			title: "他イベント時間重複",
			key: "timeOverlap",
			dataIndex: "timeOverlap",
			width: 150,
			align: "center",
			render: (overlap: boolean | undefined) =>
				overlap ? (
					<span style={{ color: primaryColor }}>〇</span>
				) : (
					<span className="text-gray-400">X</span>
				),
		},
		{
			title: "複数人予約",
			key: "allowMultipleBookings",
			dataIndex: "allowMultipleBookings",
			width: 120,
			align: "center",
			render: (allowed: boolean | undefined) =>
				allowed ? (
					<span style={{ color: primaryColor }}>〇</span>
				) : (
					<span className="text-gray-400">X</span>
				),
		},
		{
			title: "料金",
			key: "price",
			dataIndex: "price",
			width: 100,
			align: "center",
			render: (price: number | null | undefined, record) =>
				formatPrice(price, record.currency),
		},
		{
			title: "独自予約メッセージ",
			key: "customReservationMessage",
			dataIndex: "customReservationMessage",
			width: 150,
			align: "center",
			render: (hasCustom: boolean | undefined) =>
				hasCustom ? (
					<span style={{ color: primaryColor }}>〇</span>
				) : (
					<span className="text-gray-400">X</span>
				),
		},
		{
			title: "独自キャンセル設定",
			key: "customCancelSettings",
			dataIndex: "customCancelSettings",
			width: 150,
			align: "center",
			render: (hasCustom: boolean | undefined) =>
				hasCustom ? (
					<span style={{ color: primaryColor }}>〇</span>
				) : (
					<span className="text-gray-400">X</span>
				),
		},
	];

	const columns = showDetailedColumns ? detailedColumns : baseColumns;
	const scrollWidth = showDetailedColumns ? 2400 : 1600;

	return (
		<div className="w-full overflow-x-auto">
			<Table
				columns={columns}
				dataSource={occasions}
				rowKey="id"
				loading={loading}
				pagination={{
					position: ["bottomCenter"],
					showSizeChanger: false,
					showQuickJumper: false,
					showTotal: (total) => `合計 ${total} 件`,
				}}
				scroll={{ x: scrollWidth }}
				onRow={(record) => ({
					onClick: () => onEventClick?.(record),
					className: onEventClick ? "cursor-pointer" : "cursor-default",
				})}
			/>
		</div>
	);
};
